import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import { Icon } from '@fluentui/react/lib/Icon';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import * as APP_CONSTANTS from '../../../shared/Constants';
import { CmicContext } from '../../../context/CmicContext';
import { ActionContext } from '../../../context/ActionContext';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FileTypeIcon from '../../../components/common/FileTypeIcon';
import { styled } from '@mui/material/styles';
import InputBox from './InputBox';
import { MessageBarType, Stack } from '@fluentui/react';
import { THEME_ID, baseFontFamilyStyle } from '../../../shared/styles';
import { COLORS } from '../../../utility/tasks/colors';
import { AiFillCheckCircle } from 'react-icons/ai';
import { isBGClrDark } from '../../../utility/UtilityFunctions';
import {
	getUnsupportedFileNamesMsg,
	hasUnsupportedFileNameChars,
	UNSUPPORTED_FILE_NAME_MSG,
} from '../../../shared/UtilityFunctions';

const ActionUploadAttachment = ({
	btnLabel = 'N/A',
	onBtnPress,
	handleSizeOfContainer,
	handleContainerSizeChange,
	isAttachmentVisible,
	isDisabled = false,
	parentContainerStyle = {},
}) => {
	const { useEffect, useState } = React;

	const { currentOfficeTheme, setShowToast } = React.useContext(CmicContext);
	const { attachmentData, selectedAttachmentsIds, itemFromEmail, setSelectedAttachmentsIds } =
		React.useContext(ActionContext);

	const [mailForPmObjects, setMailForPmObjects] = useState([]);
	const [mailInfo, setMailInfo] = useState({ attachments: [], id: '' });
	const [selectedAttachments, setSelectedAttachments] = useState([]);
	const [selectedIds, setSelectedIds] = useState({});

	//Style

	const fixedStyle = {
		backgroundColor: currentOfficeTheme.bodyBackgroundColor,
		color: currentOfficeTheme.bodyForegroundColor,
		display: 'flex',
		flex: 1,
		padding: '0 0 0.1rem 0',
		justifyContent: 'space-between',
	};

	const headerContainer = {
		display: 'inline-flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '0 0.1rem',
		marginBottom: '0.5rem',
		marginTop: '0.6rem',
		flex: 0.2,
		maxHeight: 25,
		cursor: 'pointer',
	};

	const iconStackStyles = {
		root: { fontSize: 14, color: currentOfficeTheme.bodyForegroundColor, cursor: 'pointer', marginRight: '2px' },
	};

	const BpIcon = styled('span')(({ theme }) => ({
		borderRadius: 3,
		width: 16,
		height: 16,
		boxShadow:
			theme.palette.mode === 'dark'
				? '0 0 0 1px rgb(16 22 26 / 40%)'
				: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
		backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
		backgroundImage:
			theme.palette.mode === 'dark'
				? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
				: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
		'.Mui-focusVisible &': {
			outline: '2px auto rgba(19,124,189,.6)',
			outlineOffset: 2,
		},
		'input:hover ~ &': {
			backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
		},
		'input:disabled ~ &': {
			boxShadow: 'none',
			background: theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
		},
	}));

	const BpCheckedIcon = styled(BpIcon)({
		backgroundColor: '#808080',
		backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
		'&:before': {
			display: 'block',
			width: 16,
			height: 16,
			backgroundImage:
				"url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
				" fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
				"1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
			content: '""',
		},
		'input:hover ~ &': {
			backgroundColor: '#808080',
		},
	});

	function BpCheckbox(props) {
		return (
			<Checkbox
				sx={{
					'&:hover': { bgcolor: 'transparent' },
				}}
				disableRipple
				color="default"
				checkedIcon={<BpCheckedIcon color="primary" />}
				icon={<BpIcon color="primary" />}
				inputProps={{ 'aria-label': 'Checkbox demo' }}
				{...props}
			/>
		);
	}

	const headerTextStyle = {
		fontFamily: baseFontFamilyStyle,
		fontSize: '15px',
		fontWeight: '400',
		fontFamily: baseFontFamilyStyle,
		color:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? currentOfficeTheme.bodyForegroundColor
				: '#5E6367',
		alignItems: 'center',
		textAlign: 'center',
	};

	const noAttachmentsText = {
		fontFamily: baseFontFamilyStyle,
		fontSize: '14px',
		fontWeight: '400',
		fontFamily: baseFontFamilyStyle,
		color:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? currentOfficeTheme.bodyForegroundColor
				: '#5E6367',
		alignItems: 'center',
		textAlign: 'center',
	};

	const primaryButtonStyles = {
		rootHovered: {
			backgroundColor: `${COLORS.BG_GREY_LIGHT} !important`,
			color: COLORS.OUTLOOK_LIGHT_BLUE,
		},
		rootDisabled: {
			borderColor: isBGClrDark() ? COLORS.OUTLOOK_LIGHT_BLUE : COLORS.OUTLOOK_DARK_BLUE,
			backgroundColor: '#E1E1E1',
			color: '#A9A9A9',
		},
	};

	const handleCallBackForInputext = (selectedId, inputText) => {
		let filteredAtachments = [];

		let ids = { ...selectedIds };

		mailForPmObjects.map(v => {
			v.attachments.map(value => {
				let requiredData = {
					messageId: v.messageId,
					subject: v.subject,
					from: v.from,
					dateTimeCreated: v.dateTimeCreated,
				};
				if (selectedId === value.id) {
					value.name = inputText;
				}
				if (!value.isInline && ids[value.id]) {
					filteredAtachments.push({ ...value, ...requiredData });
				}
			});
			return v;
		});

		setSelectedAttachments(filteredAtachments);
	};

	useEffect(() => {
		let item = { ...itemFromEmail };

		if (item.messageId !== undefined) {
			fetchMailData();
			const attachments = item.attachments.map(att => {
				if (att.name === 'image' && att.name.includes('.') === false) {
					att.name = att.name + '.jpg';
				}
				return att;
			});
			item.attachments = attachments;
			setMailForPmObjects([item]);

			// Inititialize checkBox with checked on Load.
			let requiredData = {
				messageId: item.messageId,
				subject: item.subject,
				from: item.from.displayName,
				dateTimeCreated: item.dateTimeCreated,
			};

			const slcAttachments = [];
			const slcIds = {};

			item.attachments.forEach(value => {
				if (!value.isInline) {
					slcAttachments.push({ ...value, ...requiredData });
					slcIds[value.id] = value.id;
				}
			});

			setSelectedAttachments(prev => [...slcAttachments, ...prev]);
			setSelectedIds(prev => ({
				...prev,
				...slcIds,
			}));
		}

		const slcAtchIds = {};

		attachmentData.forEach(value => {
			slcAtchIds[value.id] = value.id;
		});

		setSelectedAttachmentsIds(prev => ({
			...prev,
			...slcAtchIds,
		}));

		handleContainerSizeChange(APP_CONSTANTS.SPLIT_SIZES.ECM);
	}, [attachmentData, itemFromEmail]);

	const handleSubmit = event => {
		event.preventDefault();

		const requiredData = {
			id: mailInfo.id,
			date: mailInfo.date,
			sender: mailInfo.sender,
			conversationId: mailInfo.conversationId,
			subject: mailInfo.subject,
		};

		const requiredAttachments = [];

		selectedAttachments.forEach(item => {
			if (item.originalName === 'image' && !item.name.includes('.png') && !item.name.includes('.jpg')) {
				item.name = item.name + '.jpg';
			}
			requiredAttachments.push(item);
		});

		// Quiting if there's no attachment selected
		if (!requiredAttachments.length)
			return setShowToast({
				isVisible: true,
				msg: `Please select atleast one attachment`,
				type: MessageBarType.info,
			});

		// Quiting if a selected attachment's name would make its upload fail
		const unsupportedNamesMsg = getUnsupportedFileNamesMsg(requiredAttachments);
		if (unsupportedNamesMsg)
			return setShowToast({
				isVisible: true,
				msg: unsupportedNamesMsg,
				type: MessageBarType.error,
			});

		onBtnPress(requiredAttachments, requiredData);
		attachmentData.map(value => {
			setSelectedAttachmentsIds(prev => ({
				[value.id]: value.id,
				...prev,
			}));
		});
	};

	const handleDocSelection = attachment => {
		const selections = [...selectedAttachments];

		const ids = { ...selectedIds };

		if (ids[attachment.id]) {
			const filteredSelections = selections.filter(data => data.id !== attachment.id);
			delete ids[attachment.id];
			setSelectedAttachments(filteredSelections);
			setSelectedIds(ids);
		} else {
			setSelectedAttachments(prev => [attachment, ...prev]);
			setSelectedIds(prev => ({
				[attachment.id]: attachment.id,
				...prev,
			}));
		}
	};

	const fetchMailData = () => {
		const mailAttachments = [];

		let mailData = Office.context.mailbox.item;

		mailData.attachments.forEach(item => {
			if (!item.isInline) {
				mailAttachments.push(item);
			}
		});

		const requiredData = {
			id: mailData.itemId,
			date: mailData.dateTimeModified,
			sender: mailData.sender,
			conversationId: mailData.conversationId,
			subject: mailData.subject,
			attachments: mailAttachments,
		};

		setMailInfo(requiredData);

		handleSizeOfContainer && handleSizeOfContainer(mailAttachments.length);
		setSelectedAttachments([]);
		setSelectedIds({});
	};

	if (!isAttachmentVisible) {
		return (
			<Stack
				style={{
					backgroundColor: currentOfficeTheme.bodyBackgroundColor,
					color: currentOfficeTheme.bodyForegroundColor,
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '0.3rem 0.1rem',
					cursor: 'pointer',
					...parentContainerStyle,
				}}
				onClick={() => handleContainerSizeChange(APP_CONSTANTS.SPLIT_SIZES.ECM)}
			>
				<Stack.Item
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
					}}
				>
					<h4 style={headerTextStyle}>Attachments from Email</h4>
				</Stack.Item>

				<Icon iconName={'chevronUnfold10'} styles={iconStackStyles} />
			</Stack>
		);
	} else if (!mailInfo.attachments.length) {
		return (
			<Stack>
				<Stack style={{ ...fixedStyle, ...parentContainerStyle }}>
					<Stack
						style={headerContainer}
						onClick={() => handleContainerSizeChange(APP_CONSTANTS.SPLIT_SIZES.MIN)}
					>
						<Stack.Item
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}
						>
							<h4 style={headerTextStyle}>Attachments from Email</h4>
						</Stack.Item>
						<Icon iconName={'chevronFold10'} styles={iconStackStyles} />
					</Stack>
					<Stack.Item
						style={{
							display: 'flex',
							margin: '0 0.1rem',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '1rem 0.5rem',
							border: `1px dashed ${currentOfficeTheme.bodyForegroundColor}`,
							opacity: '0.5',
						}}
					>
						<h4 style={noAttachmentsText}>No attachments found</h4>
					</Stack.Item>
					<Stack.Item
						style={{
							flex: 1,
							padding: '0 0.1rem',
							paddingTop: '10px',
							backgroundColor: currentOfficeTheme.bodyBackgroundColor,
						}}
					>
						<PrimaryButton
							styles={primaryButtonStyles}
							style={{
								background: COLORS.OUTLOOK_PRIMARY_BUTTON_COLOR,
								borderColor: COLORS.OUTLOOK_PRIMARY_BUTTON_BORDER_COLOR,
								width: '100%',
								fontWeight: 'bolder',
								fontSize: '16px',
								borderRadius: '0px',
								cursor: isDisabled ? 'not-allowed' : 'pointer',
							}}
							text={btnLabel}
							onClick={handleSubmit}
							disabled={isDisabled}
						/>
					</Stack.Item>
				</Stack>
			</Stack>
		);
	}

	return (
		<Stack style={{ ...parentContainerStyle }}>
			<Stack style={fixedStyle}>
				<Stack onClick={() => handleContainerSizeChange(APP_CONSTANTS.SPLIT_SIZES.MIN)} style={headerContainer}>
					<Stack.Item
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
						}}
					>
						<h4 style={headerTextStyle}>Attachments from Email</h4>
					</Stack.Item>
					<Icon iconName={'chevronFold10'} styles={iconStackStyles} />
				</Stack>
				<div
					id="Attachmentcontainer"
					style={{
						overflowY: 'scroll',
						maxHeight: '100px',
						padding: '0 0.1rem',
						paddingBottom: isAttachmentVisible ? '8px' : '0px',
					}}
				>
					{mailForPmObjects.map(v => {
						let requiredData = {
							messageId: v.messageId,
							subject: v.subject,
							from: v.from,
							dateTimeCreated: v.dateTimeCreated,
						};
						return (
							<div
								key={v.messageId}
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									fontFamily: baseFontFamilyStyle,
									fontSize: '12px',
								}}
							>
								{v.attachments.map((value, index) => {
									const labelId = `checkbox-list-label-${value.id}`;

									if (!value.isInline) {
										return (
											<div
												key={value.id || index}
												id="row"
												style={{
													display: 'flex',
													flex: '0 0 45%',
													marginRight: '16px',
													padding: '5px 0px',
													marginLeft: '0px',
												}}
											>
												<div>
													{!(selectedAttachmentsIds[value.id] === value.id) ? (
														<BpCheckbox
															sx={{
																width: '32px',
																margin: '0px',
																paddingBottom: '11px',
																paddingRight: '0px',
																paddingLeft: '0px',
																paddingRight: '0px',
															}}
															edge="start"
															tabIndex={-1}
															disableRipple
															inputProps={{ 'aria-labelledby': labelId }}
															onChange={() =>
																handleDocSelection({ ...value, ...requiredData })
															}
															checked={selectedIds[value.id] === value.id}
														/>
													) : (
														<span>
															<AiFillCheckCircle
																color="success"
																style={{
																	color: '#4CAF50',
																	height: '20px',
																	width: '20px',
																	marginTop: '7px',
																	marginRight: '6px',
																	marginLeft: '2px',
																}}
																fontSize="small"
															/>
														</span>
													)}
												</div>
												<span style={{ paddingTop: '5px', marginRight: '6px' }}>
													<FileTypeIcon
														name={value.name}
														iconStyles={{ margin: '0.5rem 0.5rem 0.5rem 0' }}
													/>
												</span>
												<div
													style={{
														width: 'auto',
														whiteSpace: 'nowrap',
														textOverflow: 'ellipsis',
													}}
												>
													<Box
														sx={{
															width: '95%',
															height: 'auto',
															fontFamily: baseFontFamilyStyle,
															fontSize: '12px',
														}}
													>
														<Tooltip
															title={
																hasUnsupportedFileNameChars(value.name) ? (
																	<div>
																		{value.name}
																		<br />
																		{`${UNSUPPORTED_FILE_NAME_MSG} Double-click to rename.`}
																	</div>
																) : (
																	value.name
																)
															}
															componentsProps={{
																tooltip: {
																	sx: {
																		color: currentOfficeTheme.bodyForegroundColor,
																		backgroundColor:
																			currentOfficeTheme.bodyBackgroundColor,
																		fontSize: '13px',
																		border: '0.5px solid grey',
																		padding: '5px',
																	},
																},
															}}
														>
															<Button style={{ textTransform: 'none', paddingLeft: 0 }}>
																<InputBox
																	value={value.name}
																	requiredData={value.id}
																	handleCallBackForInputext={inputText =>
																		handleCallBackForInputext(value.id, inputText)
																	}
																/>
															</Button>
														</Tooltip>
													</Box>
												</div>
											</div>
										);
									}
								})}
							</div>
						);
					})}
				</div>
			</Stack>

			<Stack.Item
				style={{
					flex: 0.5,
					padding: '0 0.1rem',
					paddingTop: '10px',
					backgroundColor: currentOfficeTheme.bodyBackgroundColor,
				}}
			>
				<PrimaryButton
					styles={primaryButtonStyles}
					style={{
						width: '100%',
						fontWeight: 'bolder',
						fontSize: '16px',
						borderColor: COLORS.OUTLOOK_PRIMARY_BUTTON_BORDER_COLOR,
						background: COLORS.OUTLOOK_PRIMARY_BUTTON_COLOR,
						borderRadius: '0px',
					}}
					text={btnLabel}
					onClick={handleSubmit}
					disabled={isDisabled}
				/>
			</Stack.Item>
		</Stack>
	);
};

export default ActionUploadAttachment;
